import { useContext, type FunctionComponent } from 'react';
import {
	type ProductPricingDisplayEnum,
	type ProductStatusEnum,
	type ProjectGroupItemType
} from '../../../__generated__/graphql-client-types';
import { ProjectsTourContext } from '../../../contexts/projects/projects-tour/projects-tour.context';
import { isShowroomOnly } from '../../../helpers/project/project.helper';
import { RequiredDateStatusTag } from '../../digital-branch-order-components/required-date-status-tag/required-date-status-tag.component';
import FergusonIsotypeIcon from '../../svgs/ferguson-isotype.svg';
import { TextBanner } from '../../text-banner/text-banner.component';

export type ProjectsTextBannerProps = {
	type: ProjectGroupItemType;
	wasPurchased: boolean;
	status: ProductStatusEnum;
	stockCount: number;
	productPricingDisplay: ProductPricingDisplayEnum;
	wasPrice: number | null;
	className?: string;
	showroomText?: string;
	showRequiredDateTag?: boolean;
	requiredDate?: Date;
	canEditDate?: boolean;
	onRequiredDateClick?: () => void;
};

export const ProjectsTextBanner: FunctionComponent<ProjectsTextBannerProps> = ({
	wasPurchased,
	type,
	status,
	stockCount,
	productPricingDisplay,
	wasPrice,
	className = '',
	showroomText,
	showRequiredDateTag,
	requiredDate,
	canEditDate,
	onRequiredDateClick
}) => {
	const {
		currentStep: { currentStepIndex }
	} = useContext(ProjectsTourContext);
	const isTourActive = currentStepIndex >= 0;

	if (isTourActive) return null;

	return (
		<div className={`flex gc2 ${showRequiredDateTag ? 'flex-row' : 'flex-column'}`}>
			{showRequiredDateTag && (
				<RequiredDateStatusTag requiredDate={requiredDate || undefined} onClick={onRequiredDateClick} isLocked={!canEditDate} />
			)}
			<TextBannerContent
				wasPurchased={wasPurchased}
				type={type}
				status={status}
				stockCount={stockCount}
				productPricingDisplay={productPricingDisplay}
				wasPrice={wasPrice}
				className={className}
				showroomText={showroomText}
			/>
		</div>
	);
};

type TextBannerContentProps = Omit<ProjectsTextBannerProps, 'requiredDate' | 'canEditDate' | 'onRequiredDateClick'>;

const TextBannerContent: FunctionComponent<TextBannerContentProps> = ({
	wasPurchased,
	type,
	status,
	stockCount,
	productPricingDisplay,
	wasPrice,
	className = '',
	showroomText
}) => {
	const showroomOnly = isShowroomOnly(productPricingDisplay);
	const isSpecialProduct = type === 'SPECIAL_PRODUCT';

	switch (true) {
		case wasPurchased:
			return <TextBanner className={className} style="success" text="Purchased" />;
		case isSpecialProduct:
			return (
				<>
					<TextBanner className={`dn db-ns ${className}`} style="alert" text="Must Purchase via your Project Expert" />
					<TextBanner className={`dn-ns ${className}`} style="alert" text="Purchase via your Project Expert" />
				</>
			);
		case status === 'discontinued':
			return <TextBanner className={className} style="alert" text="Discontinued" />;
		case showroomOnly:
			return (
				<TextBanner
					className={className}
					style="ferguson"
					icon={<FergusonIsotypeIcon className="pr1" />}
					text={showroomText || 'Ferguson Showroom Only'}
				/>
			);
		case stockCount < 1:
			return <TextBanner className={className} style="alert" text="Out of Stock" />;
		case Boolean(wasPrice):
			return <TextBanner className={`${className} flex`} style="error" text="Sale" />;
		default:
			return null;
	}
};
